/**
 * gRPC-Web Service Layer
 * Provides clean API for React components to communicate with gRPC server via Envoy
 */

import { DataLakeServiceClient } from '../generated/DatalakeServiceClientPb.ts';
import {
  DataCurationPipelineRequest,
  KeyValue,
} from '../generated/datalake_pb.js';
import { Empty } from 'google-protobuf/google/protobuf/empty_pb.js';

// Envoy proxy URL
const GRPC_WEB_URL = process.env.REACT_APP_DATALAKE_GRPC_URL || 'http://129.128.215.152:8080';

// Create singleton client instance
const client = new DataLakeServiceClient(GRPC_WEB_URL, null, null);

/**
 * Health Check - Test gRPC server connectivity
 */
export const healthCheck = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    const request = new Empty();

    client.healthCheck(request, {}, (err, response) => {
      if (err) {
        console.error('[gRPC-Web] HealthCheck error:', err);
        reject(err);
      } else {
        console.log('[gRPC-Web] HealthCheck success:', response.toObject());
        resolve(response.toObject());
      }
    });
  });
};

/**
 * Start Data Curation Pipeline with streaming updates
 */
export const startDataCurationPipeline = (
  pipelineParams: any,
  onUpdate: (update: any) => void,
  onComplete: (response: any) => void,
  onError: (error: any) => void
) => {
  console.log('[gRPC-Web] Starting Data Curation Pipeline...');
  console.log('[gRPC-Web] Pipeline params:', pipelineParams);

  // Build gRPC request
  const request = new DataCurationPipelineRequest();

  // Set basic fields
  request.setCreator(pipelineParams.creator || '');
  request.setPurpose(pipelineParams.purpose || '');
  request.setModelName(pipelineParams.model_name || '');
  request.setUuid(pipelineParams.uuid || '');
  request.setIsFinal(pipelineParams.is_final || false);
  request.setIsOriginal(pipelineParams.is_original || true);
  request.setVendorName(pipelineParams.vendor_name || 'unknown');
  request.setAnnotationType(pipelineParams.annotation_type || 'semantic_segmentation');
  request.setAnnotationFormat(pipelineParams.annotation_format || 'png');
  request.setAddAnnotationsFormat(pipelineParams.add_annotations_format || 'png');
  request.setParentId(pipelineParams.parent_id || '');
  request.setWeedThresholdPercentage(pipelineParams.weed_threshold_percentage || 20.0);

  // Add select_images_criteria as KeyValue pairs
  const selectCriteria = pipelineParams.select_images_criteria || {};
  const keyValueList: KeyValue[] = [];

  Object.keys(selectCriteria).forEach((key) => {
    if (selectCriteria[key]) {
      const kv = new KeyValue();
      kv.setKey(key);
      kv.setValue(String(selectCriteria[key]));
      keyValueList.push(kv);
    }
  });

  request.setSelectImagesCriteriaList(keyValueList);

  console.log('[gRPC-Web] Request built, calling DataCurationPipelineStream...');

  // Use streaming RPC
  try {
    const stream = client.dataCurationPipelineStream(request, {});

    stream.on('data', (update) => {
      console.log('[gRPC-Web] Stream update received:', update.toObject());
      onUpdate(update.toObject());
    });

    stream.on('end', () => {
      console.log('[gRPC-Web] Stream ended');
      // Stream completed successfully
    });

    stream.on('error', (error) => {
      console.error('[gRPC-Web] Stream error:', error);
      onError(error);
    });

    stream.on('status', (status) => {
      console.log('[gRPC-Web] Stream status:', status);
      if (status.code === 0) {
        // Success
        console.log('[gRPC-Web] Pipeline completed successfully');
      }
    });

    return stream; // Return stream so it can be cancelled
  } catch (error) {
    console.error('[gRPC-Web] Exception starting pipeline:', error);
    onError(error);
    return null;
  }
};

/**
 * Start Data Curation Pipeline (non-streaming version for fallback)
 */
export const startDataCurationPipelineNonStreaming = (
  pipelineParams: any
): Promise<any> => {
  return new Promise((resolve, reject) => {
    console.log('[gRPC-Web] Starting Data Curation Pipeline (non-streaming)...');

    const request = new DataCurationPipelineRequest();

    // Set basic fields
    request.setCreator(pipelineParams.creator || '');
    request.setPurpose(pipelineParams.purpose || '');
    request.setModelName(pipelineParams.model_name || '');
    request.setUuid(pipelineParams.uuid || '');
    request.setIsFinal(pipelineParams.is_final || false);
    request.setIsOriginal(pipelineParams.is_original || true);
    request.setVendorName(pipelineParams.vendor_name || 'unknown');
    request.setAnnotationType(pipelineParams.annotation_type || 'semantic_segmentation');
    request.setAnnotationFormat(pipelineParams.annotation_format || 'png');
    request.setAddAnnotationsFormat(pipelineParams.add_annotations_format || 'png');
    request.setParentId(pipelineParams.parent_id || '');
    request.setWeedThresholdPercentage(pipelineParams.weed_threshold_percentage || 20.0);

    // Add select_images_criteria
    const selectCriteria = pipelineParams.select_images_criteria || {};
    const keyValueList: KeyValue[] = [];

    Object.keys(selectCriteria).forEach((key) => {
      if (selectCriteria[key]) {
        const kv = new KeyValue();
        kv.setKey(key);
        kv.setValue(String(selectCriteria[key]));
        keyValueList.push(kv);
      }
    });

    request.setSelectImagesCriteriaList(keyValueList);

    client.dataCurationPipeline(request, {}, (err, response) => {
      if (err) {
        console.error('[gRPC-Web] Pipeline error:', err);
        reject(err);
      } else {
        console.log('[gRPC-Web] Pipeline success:', response.toObject());
        resolve(response.toObject());
      }
    });
  });
};

export default {
  healthCheck,
  startDataCurationPipeline,
  startDataCurationPipelineNonStreaming,
};
